import React, { useState, useEffect } from 'react';
import { X, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { enableAnalytics, disableAnalytics } from '../hooks/useGoogleAnalytics';

/**
 * Cookie Consent Banner Component
 * Provides HIPAA-compliant user consent management for Google Analytics
 * Includes detailed privacy information and granular consent options
 */
const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a consent decision
    const consentDecision = localStorage.getItem('analytics_consent');
    const consentTimestamp = localStorage.getItem('analytics_consent_timestamp');
    
    // Show banner if no decision made or decision is older than 1 year
    if (!consentDecision) {
      setIsVisible(true);
    } else if (consentTimestamp) {
      const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
      if (parseInt(consentTimestamp) < oneYearAgo) {
        setIsVisible(true);
      }
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('analytics_consent_timestamp', Date.now()?.toString());
    enableAnalytics();
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('analytics_consent_timestamp', Date.now()?.toString());
    disableAnalytics();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t-2 border-blue-600 shadow-2xl">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 mt-1">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Privacy & Analytics Consent
              </h3>
              
              <p className="text-sm text-gray-700 mb-4">
                ORA FUSION AI uses Google Analytics to improve your experience. We are committed to HIPAA compliance and your privacy.
              </p>
              
              {/* Privacy Highlights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">IP addresses anonymized</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">No PHI collected</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">Advertising disabled</span>
                </div>
              </div>
              
              {/* Detailed Information (Collapsible) */}
              {showDetails && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm text-gray-700">
                  <h4 className="font-semibold mb-2">What We Track:</h4>
                  <ul className="list-disc list-inside space-y-1 mb-3">
                    <li>Page navigation and feature usage patterns</li>
                    <li>Session duration and user interactions</li>
                    <li>Error rates and performance metrics</li>
                    <li>General demographic information (non-identifying)</li>
                  </ul>
                  
                  <h4 className="font-semibold mb-2">What We DON'T Track:</h4>
                  <ul className="list-disc list-inside space-y-1 mb-3">
                    <li>Patient names, IDs, or medical record numbers</li>
                    <li>Clinical data, diagnoses, or treatment information</li>
                    <li>Insurance or payment information</li>
                    <li>Any protected health information (PHI)</li>
                  </ul>
                  
                  <div className="flex items-start gap-2 bg-blue-50 p-3 rounded border border-blue-200">
                    <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-900">
                      <strong>HIPAA Compliance:</strong> Our analytics implementation includes automatic PHI detection and sanitization. 
                      All data is screened before transmission to ensure no protected health information is shared.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Toggle Details */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-700 underline mb-4"
              >
                {showDetails ? 'Hide Details' : 'Show More Details'}
              </button>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAccept}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Accept Analytics
                </button>
                <button
                  onClick={handleDecline}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
                >
                  Decline
                </button>
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium text-sm underline"
                >
                  Google Privacy Policy
                </a>
              </div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={handleDecline}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;